import * as React from 'react';

import type { Hits, SuggestionHits } from 'bundles/search-common/types/autocomplete';

import _t from 'i18n!nls/search-common';

type Props = {
  isFocused: boolean;
  isLoading?: boolean;
  ariaContent: ({ sectionTitle?: string | boolean; hits?: SuggestionHits[] } & Hits)[];
};

// NOTE: The autocomplete component is lacking in screen reading aria support, so
//       we're building out our own content for screen reader here.

const AutoCompleteAriaSuggestions = ({ ariaContent, isFocused, isLoading }: Props) => {
  const [mainSections, ...remainingSections] = ariaContent;

  if (!isFocused) return null;

  const mainHits = mainSections?.hits || [];
  const hasMainHits = mainSections && !mainSections.sectionTitle && mainHits?.length > 0;
  const hasRemainingSections = remainingSections?.length > 0;
  const shouldPluralizeMainHits = mainHits?.length > 1;

  const secondSectionTitle = (remainingSections[0]?.sectionTitle || '').toLowerCase();
  const isSecondSectionRecent = secondSectionTitle.includes('recent');
  const isSecondSectionPopular = secondSectionTitle.includes('popular');

  const interactionNote = _t('Use up and down arrow to navigate or enter to search');

  // For text generation, we build the entire string at once. This prevents the screen reader
  // from accidentally reading things out of order.
  let defaultSuggestionsText = _t('Popular Suggestions, #{interactionNote}', { interactionNote });
  if (isSecondSectionRecent)
    defaultSuggestionsText = _t('Popular Suggestions and Recent Searches, #{interactionNote}', { interactionNote });
  if (isSecondSectionPopular)
    defaultSuggestionsText = _t('Popular Suggestions and Popular Searches, #{interactionNote}', { interactionNote });

  const hitAndOther = shouldPluralizeMainHits
    ? _t('#{numOfSuggestions} suggestions and other sections, #{interactionNote}', {
        numOfSuggestions: mainHits.length,
        interactionNote,
      })
    : _t('1 suggestion and other sections, #{interactionNote}', { interactionNote });

  const mainHitText = hasRemainingSections ? hitAndOther : _t('1 suggestion, #{interactionNote}', { interactionNote });

  let ariaSuggestions = hasMainHits ? mainHitText : defaultSuggestionsText;
  if (!ariaContent) ariaSuggestions = '';
  const ariaText = isLoading ? _t('Loading') : ariaSuggestions;

  return <div>{ariaText}</div>;
};

export default React.memo(AutoCompleteAriaSuggestions);
